import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Card,
  DataStatus,
  FlagProps,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {Grid, Right, VStack} from 'platform/foundation';

import {ReactNode, useMemo} from 'react';
import {useFormState} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {useGetAvailableLanguagesListQuery} from '@dms/api/base';
import {
  DescriptionGeneratorConfigResponseBody,
  Language,
  PlatformDescriptionGeneratorConfigOverrides,
  useGetPlatformDescriptionGeneratorConfigQuery,
  useResetPlatformDescriptionGeneratorConfigMutation,
  useUpdatePlatformDescriptionGeneratorConfigMutation,
} from '@dms/api/settings';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri} from '@dms/routes';
import {AiVehicleDescriptionBanner, handleApiError} from '@dms/shared';

import {ApiError, buildArray, suffixTestId, TestIdProps} from 'shared/web';

type PlatformAiDescriptionFormValues = {
  enabled: boolean;
  language: Language;
  includeModel: boolean;
  includeEngine: boolean;
  includeFeatures: boolean;
  includeCondition: boolean;
  includeBranch: boolean;
  length: number;
  mentionText: string | null;
  additionalInstruction: string | null;
};

type OverridableFieldName = keyof PlatformAiDescriptionFormValues;

const OVERRIDABLE_FIELDS: readonly OverridableFieldName[] = [
  'enabled',
  'language',
  'includeModel',
  'includeEngine',
  'includeFeatures',
  'includeCondition',
  'includeBranch',
  'length',
  'mentionText',
  'additionalInstruction',
];

const hasAnyOverride = (overrides: PlatformDescriptionGeneratorConfigOverrides | undefined) =>
  isNotNil(overrides) &&
  OVERRIDABLE_FIELDS.filter((field) => field !== 'enabled').some(
    (field) => overrides[field] !== null
  );

const resolveConfig = (
  overrides: PlatformDescriptionGeneratorConfigOverrides,
  global: DescriptionGeneratorConfigResponseBody
): DescriptionGeneratorConfigResponseBody => ({
  // `enabled` intentionally does NOT inherit from global: a platform is active
  // only when it has been explicitly turned on for that platform. A null or
  // false override means off, regardless of the global toggle. Every other
  // field still falls back to global when the platform has no override.
  enabled: overrides.enabled ?? false,
  language: overrides.language ?? global.language,
  includeModel: overrides.includeModel ?? global.includeModel,
  includeEngine: overrides.includeEngine ?? global.includeEngine,
  includeFeatures: overrides.includeFeatures ?? global.includeFeatures,
  includeCondition: overrides.includeCondition ?? global.includeCondition,
  includeBranch: overrides.includeBranch ?? global.includeBranch,
  length: overrides.length ?? global.length,
  mentionText: overrides.mentionText ?? global.mentionText,
  additionalInstruction: overrides.additionalInstruction ?? global.additionalInstruction,
});

const buildRequestBody = (
  values: PlatformAiDescriptionFormValues,
  initialOverrides: PlatformDescriptionGeneratorConfigOverrides,
  globalConfig: DescriptionGeneratorConfigResponseBody
): PlatformDescriptionGeneratorConfigOverrides => {
  const pick = <K extends OverridableFieldName>(
    field: K
  ): PlatformAiDescriptionFormValues[K] | null => {
    // Already an override → preserve whatever the user has in the form (their latest edit, or the existing override value if untouched).
    if (initialOverrides[field] !== null) {
      return values[field];
    }
    // Inheriting from global → only become an override if the form value actually diverges from the global config.
    if (values[field] === globalConfig[field]) {
      return null;
    }
    return values[field];
  };
  return {
    enabled: pick('enabled'),
    language: pick('language'),
    includeModel: pick('includeModel'),
    includeEngine: pick('includeEngine'),
    includeFeatures: pick('includeFeatures'),
    includeCondition: pick('includeCondition'),
    includeBranch: pick('includeBranch'),
    length: pick('length'),
    mentionText: pick('mentionText'),
    additionalInstruction: pick('additionalInstruction'),
  };
};

export interface PlatformAiDescriptionControlProps extends TestIdProps {
  platformCode: AdvertisingPlatformCodeEnum;
}

export function PlatformAiDescriptionControl(props: PlatformAiDescriptionControlProps) {
  const {workspace} = getWorkspaceFromUri();
  const {
    data: platformConfig,
    isLoading: isLoadingPlatformConfig,
    isError,
  } = useGetPlatformDescriptionGeneratorConfigQuery({
    platformCode: props.platformCode,
  });
  const [updateConfig, {isLoading: isUpdating}] =
    useUpdatePlatformDescriptionGeneratorConfigMutation();
  const [resetConfig, {isLoading: isResetting}] =
    useResetPlatformDescriptionGeneratorConfigMutation();
  const {data: languageData} = useGetAvailableLanguagesListQuery(
    isNotNil(workspace) ? {workspace} : skipToken
  );

  const languageOptions = Object.entries(languageData?.languages ?? {}).map(([value, label]) => ({
    label,
    value,
  }));

  const onSubmit: FormSubmitHandler<PlatformAiDescriptionFormValues> = (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    if (!platformConfig) {
      return Promise.resolve();
    }

    const body = buildRequestBody(values, platformConfig.overrides, platformConfig.global);

    return updateConfig({platformCode: props.platformCode, body})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const handleToggleEnabled = (isEnabled: boolean) => {
    if (!platformConfig) {
      return;
    }

    updateConfig({
      platformCode: props.platformCode,
      body: {...platformConfig.overrides, enabled: isEnabled},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleReset = () => {
    openConfirmDialog({
      text: i18n.t('entity.advertisingPlatforms.labels.aiDescriptionResetConfirm'),
      onConfirm: () =>
        resetConfig({platformCode: props.platformCode})
          .unwrap()
          .then(() =>
            showNotification.success(
              i18n.t('entity.advertisingPlatforms.notifications.aiDescriptionReset')
            )
          )
          .catch(handleApiError),
    });
  };

  // Stable reference is mandatory: the platform Form passes `values` straight
  // to react-hook-form, which resets the form (clearing dirtyFields) every time
  // the reference changes. A new object literal per render would wipe the
  // user's edits before they could be submitted.
  const resolved = useMemo(
    () => (platformConfig ? resolveConfig(platformConfig.overrides, platformConfig.global) : null),
    [platformConfig]
  );

  const isEnabled = resolved?.enabled ?? false;

  const anyOverride = hasAnyOverride(platformConfig?.overrides);

  const cardActions = buildArray<Action>().when(isEnabled && anyOverride, {
    type: 'button',
    variant: 'dangerLink',
    leftIcon: 'navigation/refresh',
    onClick: handleReset,
    isLoading: isResetting,
    title: i18n.t('entity.advertisingPlatforms.labels.aiDescriptionResetToGlobal'),
    'data-testid': suffixTestId('reset', props),
  });

  const flags = buildArray<FlagProps>()
    .when(isEnabled && anyOverride, {
      label: i18n.t('entity.advertisingPlatforms.labels.aiDescriptionCustomActive'),
      colorScheme: 'purple',
    })
    .when(isEnabled && !anyOverride, {
      label: i18n.t('entity.advertisingPlatforms.labels.aiDescriptionUsingGlobal'),
      colorScheme: 'neutral',
    });

  return (
    <Card
      title={i18n.t('entity.advertisingPlatforms.labels.aiVehicleDescription')}
      subtitle={i18n.t('entity.advertisingPlatforms.labels.aiDescriptionPerPlatformHelp')}
      isExpandable={false}
      isExpanded={isEnabled}
      hasSeparator={false}
      variant="inlineGrey"
      control={{
        type: 'switch',
        value: isEnabled,
        isLoading: isLoadingPlatformConfig || isUpdating,
        onChange: handleToggleEnabled,
      }}
      actions={cardActions}
      flags={flags}
      data-testid={props['data-testid']}
    >
      <DataStatus isLoading={isLoadingPlatformConfig} isError={isError}>
        <VStack spacing={4}>
          <AiVehicleDescriptionBanner />
          {platformConfig && resolved ? (
            <Form<PlatformAiDescriptionFormValues>
              values={resolved}
              onSubmit={onSubmit}
              shouldWatchForUnsavedChanges
            >
              {(control) => (
                <VStack spacing={4}>
                  <Grid columns={2}>
                    <VStack spacing={4}>
                      <OverrideWrapper
                        control={control}
                        name="includeModel"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="includeModel"
                            control={control}
                            type="checkbox"
                            label={`${i18n.t('entity.advertisingPlatforms.labels.includeModel')}${sourceSuffix}`}
                            helperText={i18n.t(
                              'entity.advertisingPlatforms.labels.includeModelDescription'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="includeEngine"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="includeEngine"
                            control={control}
                            type="checkbox"
                            label={`${i18n.t('entity.advertisingPlatforms.labels.includeEngine')}${sourceSuffix}`}
                            helperText={i18n.t(
                              'entity.advertisingPlatforms.labels.includeEngineDescription'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="includeFeatures"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="includeFeatures"
                            control={control}
                            type="checkbox"
                            label={`${i18n.t('entity.advertisingPlatforms.labels.includeFeatures')}${sourceSuffix}`}
                            helperText={i18n.t(
                              'entity.advertisingPlatforms.labels.includeFeaturesDescription'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="includeCondition"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="includeCondition"
                            control={control}
                            type="checkbox"
                            label={`${i18n.t('entity.advertisingPlatforms.labels.includeCondition')}${sourceSuffix}`}
                            helperText={i18n.t(
                              'entity.advertisingPlatforms.labels.includeConditionDescription'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="includeBranch"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="includeBranch"
                            control={control}
                            type="checkbox"
                            label={`${i18n.t('entity.advertisingPlatforms.labels.includeBranch')}${sourceSuffix}`}
                            helperText={i18n.t(
                              'entity.advertisingPlatforms.labels.includeBranchDescription'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                    </VStack>
                    <VStack spacing={4}>
                      <OverrideWrapper
                        control={control}
                        name="length"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="length"
                            control={control}
                            type="number"
                            decimalPlaces={0}
                            label={`${i18n.t('entity.advertisingPlatforms.labels.descriptionLength')}${sourceSuffix}`}
                            tooltip={i18n.t(
                              'entity.advertisingPlatforms.labels.descriptionLengthTooltip'
                            )}
                            isRequired
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="mentionText"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="mentionText"
                            control={control}
                            type="textarea"
                            isResizable
                            label={`${i18n.t('entity.advertisingPlatforms.labels.mentionText')}${sourceSuffix}`}
                            tooltip={i18n.t(
                              'entity.advertisingPlatforms.labels.mentionTextTooltip'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="additionalInstruction"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="additionalInstruction"
                            control={control}
                            type="textarea"
                            isResizable
                            label={`${i18n.t('entity.advertisingPlatforms.labels.additionalInstruction')}${sourceSuffix}`}
                            tooltip={i18n.t(
                              'entity.advertisingPlatforms.labels.additionalInstructionTooltip'
                            )}
                          />
                        )}
                      </OverrideWrapper>
                      <OverrideWrapper
                        control={control}
                        name="language"
                        initialOverrides={platformConfig.overrides}
                      >
                        {(sourceSuffix) => (
                          <FormField
                            name="language"
                            control={control}
                            type="choice"
                            isRequired
                            isNotClearable
                            label={`${i18n.t('entity.advertisingPlatforms.labels.language')}${sourceSuffix}`}
                            options={languageOptions}
                          />
                        )}
                      </OverrideWrapper>
                    </VStack>
                  </Grid>
                  <Right>
                    <FormButton
                      control={control}
                      type="submit"
                      isLoading={isUpdating}
                      title={i18n.t('general.actions.save')}
                      data-testid={suffixTestId('save', props)}
                    />
                  </Right>
                </VStack>
              )}
            </Form>
          ) : null}
        </VStack>
      </DataStatus>
    </Card>
  );
}

interface OverrideWrapperProps {
  control: FormControl<PlatformAiDescriptionFormValues>;
  name: OverridableFieldName;
  initialOverrides: PlatformDescriptionGeneratorConfigOverrides;
  children: (sourceSuffix: string) => ReactNode;
}

/**
 * Wraps a per-platform AI form field and computes a label suffix that
 * indicates where the field's value currently comes from — global defaults
 * (default) or a custom platform-level override. The suffix is composed into
 * the FormField's `label` prop by the consumer so the indicator lives inside
 * the label itself.
 */
function OverrideWrapper(props: OverrideWrapperProps) {
  const {dirtyFields} = useFormState({control: props.control, name: props.name});
  const wasOverridden = props.initialOverrides[props.name] !== null;
  const isDirty = Boolean(dirtyFields[props.name]);
  const isOverridden = wasOverridden || isDirty;
  const sourceSuffix = ` (${i18n.t(
    isOverridden
      ? 'entity.advertisingPlatforms.labels.aiDescriptionFieldOverride'
      : 'entity.advertisingPlatforms.labels.aiDescriptionFieldInherits'
  )})`;
  return <>{props.children(sourceSuffix)}</>;
}
